import {
  ContextSelectedOptions,
  ContextSelector
} from "@appretio/context-selector";
import { Button, ExpandSheetButton, Sheet, SheetTabs } from "@appretio/ui-kit";
import {
  GetRowIdParams,
  RowSelectedEvent,
  SortModelItem
} from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { ReviewDataTable } from "../../components/reviewDataTable/ReviewDataTable";
import { TemporalContextSelector } from "../../components/temporalContextSelector/TemporalContextSelector";
import {
  ReviewDataResponse,
  useFetchReviewData
} from "./hooks/useFetchReviewData";
import { TopTenResponse } from "./hooks/useFetchTopTen";
import { useSheetTabs } from "./hooks/useSheetTabs";
import {
  INSEASON_CONTEXT_FIELDS,
  INSEASON_TABS,
  INSEASON_TEMPORAL_PRESELECTED_OPTIONS,
  PAGE_SIZE
} from "./inSeasonReviewConfig";
import {
  contextActionsContainer,
  contextContainer,
  contextSelectorsContainer,
  customSheetStyleNav
} from "./InSeasonReviewPage.css";
import { AccuracyTab } from "./tabs/AccuracyTabContent";
import { CategoryTab } from "./tabs/CategoryTabContent";
import { ColourTab } from "./tabs/ColourTabContent";
import { PriceTab } from "./tabs/PriceTabContent";
import { TopTenTabContent } from "./tabs/TopTenTabContent";
import {
  createServerSideDataSource,
  extractSortModelFromGrid
} from "./utils/gridServerSideUtils";

export function InSeasonReviewPage() {
  const [selectedContextOptions, setSelectedContextOptions] =
    useState<ContextSelectedOptions>({});

  const [selectedTemporalOptions, setSelectedTemporalOptions] = useState(
    INSEASON_TEMPORAL_PRESELECTED_OPTIONS
  );

  // Applied state (only changes when Apply button is clicked)
  const hasUserAppliedRef = useRef(false);
  const [appliedFilters, setAppliedFilters] = useState({
    contextOptions: {} as ContextSelectedOptions,
    temporalOptions: INSEASON_TEMPORAL_PRESELECTED_OPTIONS
  });

  // Reset hasUserAppliedRef after dropdown changes
  useEffect(() => {
    if (hasUserAppliedRef.current) {
      hasUserAppliedRef.current = false;
    }
  }, [selectedContextOptions, selectedTemporalOptions]);

  const [sortingOptions, setSortingOptions] = useState<SortModelItem[]>([]);

  // Use useInfiniteQuery for grid data fetching
  const { data, isFetching, fetchNextPage } = useFetchReviewData(
    appliedFilters.contextOptions,
    appliedFilters.temporalOptions,
    sortingOptions,
    hasUserAppliedRef.current
  );
  const reviewData = data?.pages.flatMap((page) => page);

  // Ref for the Ag-Grid instance to access the API
  const gridRef = useRef<AgGridReact>(null);
  // Unique rowId is required for Server-Side Row Model
  const getRowId = useCallback(
    (params: GetRowIdParams) => String(params.data.style_option_number),
    []
  );

  // Ref to hold all rows for quick access in serverSideDatasource
  const currentDataRef = useRef<ReviewDataResponse>([]);
  // Update ref whenever data changes
  useEffect(() => {
    if (data?.pages) {
      currentDataRef.current = data.pages.flatMap((page) => page);
    }
  }, [data]);

  // Reset grid state and set new datasource
  const resetGridState = useCallback(() => {
    // Reset grid state before new datasource
    setSelectedRowData([]);

    gridRef.current?.api.deselectAll();
    currentDataRef.current = [];

    // Create and set new datasource for the grid
    const serverSideDataSource = createServerSideDataSource(
      fetchNextPage,
      currentDataRef
    );
    gridRef.current?.api.setGridOption(
      "serverSideDatasource",
      serverSideDataSource
    );
  }, [fetchNextPage, currentDataRef]);

  // Handle Apply button click
  const handleApply = useCallback(() => {
    setAppliedFilters({
      contextOptions: selectedContextOptions,
      temporalOptions: selectedTemporalOptions
    });

    hasUserAppliedRef.current = true;

    // Reset chart drill-down state when loading new data
    setChartSelectedCategory(undefined);

    resetGridState();
  }, [resetGridState, selectedContextOptions, selectedTemporalOptions]);

  // Handle Clear button click
  const handleClear = useCallback(() => {
    setSelectedContextOptions({});
    setSelectedTemporalOptions(INSEASON_TEMPORAL_PRESELECTED_OPTIONS);
  }, []);

  // Tabs management
  const { activeTab, handleTabChange } = useSheetTabs(INSEASON_TABS);

  // Set initial grid state
  const handleGridReady = useCallback(() => {
    if (gridRef.current?.api) {
      // Show "No Rows" overlay
      gridRef.current.api.showNoRowsOverlay();
    }
  }, []);

  // Handle sort changes via AG-Grid's onSortChanged event
  const handleSortChanged = useCallback(() => {
    const sortModel = extractSortModelFromGrid(gridRef);
    setSortingOptions(sortModel);
    resetGridState();
  }, [resetGridState]);

  // Manage selected rows
  const [selectedRowData, setSelectedRowData] = useState<ReviewDataResponse>(
    []
  );
  const handleRowSelected = useCallback((event: RowSelectedEvent) => {
    const selectedRows = event.api.getSelectedRows();
    setSelectedRowData(selectedRows);
  }, []);

  // Manage chart category selection for drill-down
  const [chartSelectedCategory, setChartSelectedCategory] = useState<
    string | undefined
  >(undefined);

  // Handle category click from chart
  const handleCategoryClick = useCallback((category: string) => {
    // Set the selected category to drill down into subcategories
    setChartSelectedCategory(category);
  }, []);

  // Handle clearing category selection (go back to category view)
  const handleClearCategorySelection = useCallback(() => {
    // Clear chart selection
    setChartSelectedCategory(undefined);
  }, []);

  return (
    <>
      <Sheet>
        <div className={contextContainer}>
          <div className={contextSelectorsContainer}>
            <ContextSelector
              fields={INSEASON_CONTEXT_FIELDS}
              selected={selectedContextOptions}
              setSelected={setSelectedContextOptions}
            />
            <TemporalContextSelector
              selected={selectedTemporalOptions}
              setSelected={setSelectedTemporalOptions}
            />
          </div>

          <div className={contextActionsContainer}>
            <Button
              variant="secondary"
              onClick={handleClear}
              disabled={isFetching}
            >
              Clear
            </Button>
            <Button
              onClick={handleApply}
              disabled={isFetching}
              isLoading={isFetching}
            >
              Apply
            </Button>
          </div>
        </div>
      </Sheet>
      <Sheet className={customSheetStyleNav}>
        <SheetTabs value={activeTab.value} onValueChange={handleTabChange}>
          <SheetTabs.Header>
            <SheetTabs.List>
              {INSEASON_TABS.map((tabItem) => (
                <SheetTabs.Trigger
                  key={`trigger-${tabItem.value}`}
                  value={tabItem.value}
                >
                  {tabItem.label}
                </SheetTabs.Trigger>
              ))}
            </SheetTabs.List>
            <ExpandSheetButton variant="tertiary" size="sm" />
          </SheetTabs.Header>

          <SheetTabs.ContentWrapper
            activeValue={activeTab.value}
            firstValue={INSEASON_TABS[0].value}
          >
            {/* Accuracy tab */}
            <SheetTabs.Content value="accuracy">
              <AccuracyTab
                appliedFilters={appliedFilters}
                hasUserApplied={hasUserAppliedRef.current}
                reviewData={
                  selectedRowData.length > 0
                    ? selectedRowData
                    : (reviewData ?? [])
                }
                isReviewDataLoading={isFetching}
              />
            </SheetTabs.Content>

            {/* Price tab */}
            <SheetTabs.Content value="price">
              <PriceTab
                appliedFilters={appliedFilters}
                hasUserApplied={hasUserAppliedRef.current}
                selectedRowData={selectedRowData}
              />
            </SheetTabs.Content>
            {/* Top Ten tab */}
            <SheetTabs.Content value="top10">
              <TopTenTabContent
                selectedRowData={selectedRowData as TopTenResponse[]}
                appliedFilters={appliedFilters}
                hasUserApplied={hasUserAppliedRef.current}
                reviewData={
                  selectedRowData.length > 0
                    ? selectedRowData
                    : (reviewData ?? [])
                }
                isReviewDataLoading={isFetching}
              />
            </SheetTabs.Content>

            {/* Colour tab */}
            <SheetTabs.Content value="colour">
              <ColourTab
                appliedFilters={appliedFilters}
                hasUserApplied={hasUserAppliedRef.current}
                selectedRowData={selectedRowData}
              />
            </SheetTabs.Content>
            {/* Category tab */}
            <SheetTabs.Content value="category">
              <CategoryTab
                appliedFilters={appliedFilters}
                hasUserApplied={hasUserAppliedRef.current}
                selectedRowData={selectedRowData}
                onCategoryClick={handleCategoryClick}
                chartSelectedCategory={chartSelectedCategory}
                onClearCategorySelection={handleClearCategorySelection}
              />
            </SheetTabs.Content>

            {/* Other tabs */}
            {INSEASON_TABS.filter(
              (tab) =>
                ![
                  "table",
                  "accuracy",
                  "price",
                  "top10",
                  "category",
                  "colour"
                ].includes(tab.value)
            ).map((tabItem) => (
              <SheetTabs.Content
                key={`content-${tabItem.value}`}
                value={tabItem.value}
              >
                <div>{tabItem.label} coming soon...</div>
              </SheetTabs.Content>
            ))}

            <ReviewDataTable
              ref={gridRef}
              rowModelType="serverSide"
              getRowId={getRowId}
              gridOptions={{
                suppressScrollOnNewData: true,
                cacheBlockSize: PAGE_SIZE,
                maxBlocksInCache: 10,
                maxConcurrentDatasourceRequests: 1, // Only one request at a time
                onGridReady: handleGridReady,
                onSortChanged: handleSortChanged
              }}
              loading={isFetching}
              onRowSelected={handleRowSelected}
            />
          </SheetTabs.ContentWrapper>
        </SheetTabs>
      </Sheet>
    </>
  );
}
